import * as Popover from '@radix-ui/react-popover';
import React, { useState } from 'react';

import {
  CATEGORY_OPTIONS,
  CATEGORY_OPTIONS_MAP,
} from '../constants/categoryOptions';

interface CategorySelectFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  isEditing: boolean;
  hasError?: boolean;
}

export const CategorySelectField: React.FC<CategorySelectFieldProps> = ({
  label,
  value,
  onChange,
  isEditing,
  hasError = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get display text from map using the category ID
  const displayText = value ? CATEGORY_OPTIONS_MAP[value] : null;

  // Filter options based on search term
  const filteredOptions = CATEGORY_OPTIONS.filter(option =>
    option.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group filtered options by their group name
  const groupedOptions = filteredOptions.reduce(
    (acc, option) => {
      // Extract group name from text (it's the part in parentheses)
      const match = option.text.match(/\(([^)]+)\)$/);
      const groupName = match ? match[1] : 'Other';

      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(option);
      return acc;
    },
    {} as Record<string, typeof CATEGORY_OPTIONS>
  );

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <div className='flex justify-between items-center'>
      {isEditing ? (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              type='button'
              className={`text-xs px-2 py-1 border rounded w-64 text-left bg-white hover:bg-gray-50 ${
                hasError && !value
                  ? 'border-red-500 text-red-600'
                  : 'border-gray-300'
              }`}
            >
              {displayText || (
                <span className={hasError ? 'text-red-600' : 'text-gray-400'}>
                  Select a category
                </span>
              )}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className='w-64 bg-white border border-gray-300 rounded shadow-lg z-50'
              sideOffset={5}
              align='start'
            >
              <div className='p-2'>
                <input
                  type='text'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder='Search categories...'
                  className='text-xs px-2 py-1 border border-gray-300 rounded w-full mb-2'
                />
                <div className='max-h-64 overflow-y-auto'>
                  {Object.keys(groupedOptions).length === 0 ? (
                    <div className='px-2 py-2 text-xs text-gray-500'>
                      No categories found
                    </div>
                  ) : (
                    Object.entries(groupedOptions).map(
                      ([groupName, options]) => (
                        <div key={groupName}>
                          <div className='px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 sticky top-0'>
                            {groupName}
                          </div>
                          {options.map(opt => (
                            <button
                              key={opt.id}
                              type='button'
                              onClick={() => handleSelect(opt.id)}
                              className={`w-full text-left px-2 py-1 text-xs cursor-pointer hover:bg-blue-50 ${
                                opt.id === value
                                  ? 'bg-blue-100 font-medium'
                                  : ''
                              }`}
                            >
                              {opt.text.replace(` (${groupName})`, '')}
                            </button>
                          ))}
                        </div>
                      )
                    )
                  )}
                </div>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : (
        <span className='font-medium text-gray-800 text-right'>
          {displayText || (
            <span className='text-red-600 italic'>Select a category</span>
          )}
        </span>
      )}
    </div>
  );
};
